import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Music2, Search, Star, Loader2, Download, RefreshCw, Play, AlertTriangle, EyeOff, Plus } from "lucide-react";
import { fetchCachedSongs, exportCachedSongs, hideSong } from "../../lib/api";
import { LabelChips } from "./LabelChips";
import { toast } from "sonner";

const SOURCES = [
  { value: "", label: "Todas las fuentes" },
  { value: "favorite", label: "Favoritas" },
  { value: "learned", label: "Biblioteca aprendida" },
  { value: "warm_cache", label: "Warm cache" },
  { value: "youtube_search", label: "Búsqueda YouTube" },
];

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("es", { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return "—";
  }
};

const SOURCE_LABEL = {
  favorite: "Favorita",
  learned: "Biblioteca",
  warm_cache: "Warm cache",
  youtube_search: "YouTube",
};

const CachedSongsDialog = ({ open, onOpenChange, onAddSong, onPlayNow }) => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [source, setSource] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCachedSongs();
      setSongs(data.songs || []);
    } catch (e) {
      toast.error("No se pudieron cargar las canciones cacheadas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Client-side filtering (fast, no extra requests)
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return songs.filter((x) => {
      if (favOnly && x.favorite_count <= 0) return false;
      if (source && x.source !== source) return false;
      if (s) {
        const hay = `${x.display_title} ${x.artist || ""} ${x.normalized_title}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [songs, search, favOnly, source]);

  const handleHide = async (song) => {
    try {
      await hideSong({
        songKey: song.song_key,
        title: song.best_version?.title || song.display_title,
        channelTitle: song.best_version?.channelTitle || "",
        artist: song.artist,
        query: song.display_title,
      });
      setSongs((prev) => prev.filter((x) => x.song_key !== song.song_key));
      toast("Canción oculta del catálogo", { description: song.display_title });
    } catch (e) {
      toast.error("No se pudo ocultar la canción");
    }
  };

  const handleExport = async (format) => {
    try {
      const data = await exportCachedSongs(format);
      let blob;
      let filename;
      if (format === "csv") {
        blob = new Blob([data], { type: "text/csv;charset=utf-8" });
        filename = `djgabo-cached-songs-${new Date().toISOString().slice(0, 10)}.csv`;
      } else {
        blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        filename = `djgabo-cached-songs-${new Date().toISOString().slice(0, 10)}.json`;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-4xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="cached-songs-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                <Music2 className="w-5 h-5 text-secondary" />
                Canciones únicas cacheadas
              </DialogTitle>
              <DialogDescription>
                Versiones de la misma canción agrupadas en una sola. {songs.length} canciones únicas.
              </DialogDescription>
            </div>
            <button
              onClick={load}
              disabled={loading}
              data-testid="cached-songs-refresh"
              className="shrink-0 flex items-center gap-1.5 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary text-xs font-medium disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Actualizar
            </button>
          </div>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtrar por canción o artista..."
              data-testid="cached-songs-search"
              className="w-full bg-white/[0.05] border border-white/15 rounded-md h-9 pl-9 pr-3 text-sm text-white focus:border-secondary focus:outline-none font-mono"
            />
          </div>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            data-testid="cached-songs-source-filter"
            className="bg-white/[0.05] border border-white/15 rounded-md h-9 px-2 text-sm text-white focus:border-secondary focus:outline-none"
          >
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value} className="bg-surface">{s.label}</option>
            ))}
          </select>
          <button
            onClick={() => setFavOnly((v) => !v)}
            data-testid="cached-songs-fav-filter"
            className={`flex items-center gap-1.5 px-3 h-9 rounded-md border text-xs font-medium transition-all ${
              favOnly ? "border-amber-400 bg-amber-400/10 text-amber-400" : "border-border hover:border-amber-400 hover:text-amber-400"
            }`}
          >
            <Star className="w-3.5 h-3.5" fill={favOnly ? "currentColor" : "none"} /> Solo favoritas
          </button>
          <div className="flex gap-1">
            <button
              onClick={() => handleExport("csv")}
              data-testid="cached-songs-export-csv"
              className="flex items-center gap-1.5 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary text-xs font-medium"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button
              onClick={() => handleExport("json")}
              data-testid="cached-songs-export-json"
              className="flex items-center gap-1.5 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary text-xs font-medium"
            >
              <Download className="w-3.5 h-3.5" /> JSON
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto border border-border rounded-md mt-1" data-testid="cached-songs-table">
          {loading ? (
            <div className="py-10 flex justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No hay canciones que coincidan.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-elevated border-b border-border z-10">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <th className="text-left p-2.5">Canción</th>
                  <th className="text-left p-2.5 hidden lg:table-cell">Mejor versión</th>
                  <th className="text-center p-2.5">Result.</th>
                  <th className="text-center p-2.5">Fav</th>
                  <th className="text-left p-2.5 hidden md:table-cell">Último uso</th>
                  <th className="text-left p-2.5 hidden sm:table-cell">Fuente</th>
                  <th className="text-right p-2.5">Acción</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((x) => (
                  <tr key={x.song_key} data-testid="cached-song-row" className="border-b border-border last:border-0 hover:bg-white/5">
                    <td className="p-2.5">
                      <p className="font-medium">{x.display_title}</p>
                      {x.artist && <p className="text-[11px] text-muted-foreground">{x.artist}</p>}
                      <LabelChips labels={x.labels} content={x.content_labels} className="mt-1" />
                    </td>
                    <td className="p-2.5 text-muted-foreground hidden lg:table-cell max-w-[240px]">
                      <span className="flex items-center gap-1 truncate" title={x.best_version?.title}>
                        {x.best_version?.isFavorite && <Star className="w-3 h-3 text-amber-400 shrink-0" fill="currentColor" />}
                        {x.best_version_warning && <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />}
                        <span className="truncate text-xs">{x.best_version?.title || "—"}</span>
                      </span>
                    </td>
                    <td className="p-2.5 text-center font-mono text-secondary">{x.versions_count}</td>
                    <td className="p-2.5 text-center">
                      {x.favorite_count > 0 ? <Star className="w-4 h-4 text-amber-400 inline" fill="currentColor" /> : <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="p-2.5 text-muted-foreground font-mono text-xs hidden md:table-cell">{fmtDate(x.last_used_at)}</td>
                    <td className="p-2.5 hidden sm:table-cell">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-white/5 border border-border rounded px-1.5 py-0.5">
                        {SOURCE_LABEL[x.source] || x.source}
                      </span>
                    </td>
                    <td className="p-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => { onOpenChange(false); onPlayNow && onPlayNow(x.best_version, x.best_version_warning); }}
                          disabled={!x.best_version?.videoId}
                          data-testid="play-now-best-version-button"
                          title="Reproducir esta versión ahora"
                          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-secondary text-secondary-foreground font-bold text-[11px] uppercase tracking-wide hover:bg-secondary/90 active:scale-95 transition-all disabled:opacity-40"
                        >
                          <Play className="w-3.5 h-3.5" fill="currentColor" /> Ahora
                        </button>
                        <button
                          onClick={() => { onOpenChange(false); onAddSong && onAddSong(x.best_version, x.best_version_warning); }}
                          disabled={!x.best_version?.videoId}
                          data-testid="add-best-version-button"
                          title="Agregar la mejor versión a la cola"
                          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-primary text-primary-foreground font-bold text-[11px] uppercase tracking-wide hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-40"
                        >
                          <Plus className="w-3.5 h-3.5" /> Cola
                        </button>
                        <button
                          onClick={() => handleHide(x)}
                          data-testid="hide-song-button"
                          title="Ocultar esta canción del catálogo"
                          className="w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors"
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground/60 font-mono shrink-0">
          Mostrando {filtered.length} de {songs.length} canciones únicas (videos de la misma canción se agrupan en una sola).
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default CachedSongsDialog;
